import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'ja' | 'en' | 'th';

/**
 * アプリのベースパス（React Router の basename と同じもの）。
 *
 * これを考慮していなかったのが、画面が真っ白になる原因だった。
 * basename が /staff-dashboard のとき、言語プレフィックスを
 * パスの先頭に付けると /ja/staff-dashboard/... になる。
 * このURLは basename で始まらないため React Router は何も描画しない。
 * しかも history.replaceState はルータに通知しないので、
 * その場では正常に見え、次の遷移やリロードで初めて白くなる。
 */
const BASE_PATH = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

/** basename を除いた部分を返す */
const stripBase = (path: string): string => {
  if (BASE_PATH && path.startsWith(BASE_PATH)) {
    return path.slice(BASE_PATH.length) || '/';
  }
  return path;
};

// URLから言語プレフィックスを取得（basename の内側を見る）
const getLanguageFromPath = (): Language | null => {
  const rest = stripBase(window.location.pathname);
  const match = rest.match(/^\/(ja|en|th)(\/|$)/);
  return match ? (match[1] as Language) : null;
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  ja: {
    // ============ ダッシュボード ============
    dashboard: 'ダッシュボード',
    reservationManagement: '予約管理',
    statistics: '統計情報',
    totalReservations: '総予約数',
    todayReservations: '本日の予約',
    monthlyReservations: '今月の予約',
    totalStaff: 'スタッフ数',
    reservationQRCode: '予約QRコード',
    qrCodeDescription: 'このQRコードをスキャンして予約ページにアクセス',
    downloadQRCode: 'QRコードをダウンロード',
    printQRCode: 'QRコードを印刷',
    viewReservations: '予約を見る',
    salesAnalytics: '売上分析',
    todaySales: '本日の売上',
    monthlySales: '今月の売上',
    totalOrders: '総注文数',

    // ============ ナビゲーション ============
    home: 'ホーム',
    reservations: '予約管理',
    menus: 'メニュー管理',
    staff: '従業員管理',
    customers: '顧客管理',
    analytics: '売上分析',
    floorMap: '空席表(MAP)',
    backApproval: 'バック承認',
    settings: '設定',
    logout: 'ログアウト',
    employees: '従業員管理',
    staffUsers: 'アプリアカウント',
    shifts: 'シフト管理',
    attendances: '勤怠管理',
    planSettings: 'プラン設定',
    timeChargeSettings: '時間制の席料',
    allShops: '店舗の一覧',
    salesReport: '売上の実績',
    stripeConnect: 'Stripe連携',
    reservationSettings: '予約設定',
    homepageSettings: 'ホームページ設定',
    shopSettings: '店舗設定',
    lineSettings: 'LINE連携',
    nightPro: '夜職Proモード',
    receivables: '未収金（ツケ）',
    bottleKeeps: 'ボトルキープ',
    proxyOrder: '代理注文',
    allowanceTypes: '手当マスタ',
    payouts: '日払い',
    payroll: '給与',
    setupCheck: '開店前チェック',
    posRegister: 'POSレジ',
    checkout: '会計',
    checkoutRegister: '会計・レジ',
    orderManagement: '注文管理',
    tableManagement: '席管理',
    blacklist: 'ブラックリスト',
    productCategories: '商品カテゴリ',
    optionManagement: 'オプション管理',
    productManagement: '商品管理',
    planManagement: 'プラン管理',
    printerSettings: 'プリンター設定',
    mobileOrderSettings: 'モバイルオーダー設定',

    // ============ 共通 ============
    save: '保存',
    cancel: 'キャンセル',
    delete: '削除',
    edit: '編集',
    add: '追加',
    search: '検索',
    filter: 'フィルター',
    close: '閉じる',
    back: '戻る',
    next: '次へ',
    previous: '前へ',
    loading: '読み込み中...',
    error: 'エラーが発生しました',
    success: '成功しました',
    confirm: '確認',
    yes: 'はい',
    no: 'いいえ',
    ok: 'OK',
    create: '作成',
    update: '更新',
    detail: '詳細',
    list: '一覧',
    all: 'すべて',
    noData: 'データがありません',
    actions: '操作',
    status: 'ステータス',
    date: '日付',
    time: '時刻',
    name: '名前',
    email: 'メールアドレス',
    phone: '電話番号',
    address: '住所',
    price: '価格',
    amount: '金額',
    total: '合計',
    subtotal: '小計',
    tax: '税',
    quantity: '数量',
    description: '説明',
    notes: '備考',
    required: '必須',
    optional: '任意',

    // ============ イベント関連 ============
    eventManagement: 'イベント管理',
    events: 'イベント',
    addEvent: 'イベントを追加',
    editEvent: 'イベントを編集',
    eventType: 'イベント種別',
    upcoming: '開催予定',
    past: '終了',
    displayPeriod: '表示期間',
    startDate: '開始日',
    endDate: '終了日',
    images: '画像',
    mainImage: 'メイン画像',
    bannerImage: 'バナー画像',
    active: '公開',
    reservationRequired: '予約必須',
    showOnCalendar: 'カレンダーに表示',
    showBanner: 'バナー表示',
    bannerPosition: 'バナー位置',
    heroPosition: 'ヒーロー（上部）',
    middlePosition: '中央',
    bottomPosition: '下部',
    remaining: '残り',
    seats: '席',
    hide: '非表示にする',
    show: '表示する',
    deleteConfirm: 'このイベントを削除してもよろしいですか？',
    title: 'タイトル',

    // ============ クーポン関連 ============
    couponManagement: 'クーポン管理',
    couponCreate: 'クーポン作成',
    couponEdit: 'クーポン編集',
    couponCode: 'クーポンコード',
    couponDiscount: '割引内容',
    couponDiscountType: '割引タイプ',
    couponPercentage: '割引率（%）',
    couponFixed: '割引額（円）',
    couponPercentageValue: '割引率（%）',
    couponFixedValue: '割引額（円）',
    couponExpiry: '有効期限',
    couponExpiryHelp: '空欄の場合は無期限',
    couponUsage: '使用回数',
    couponMaxUsage: '利用回数上限',
    couponMaxUsageHelp: '0の場合は無制限',
    couponMinOrder: '最低利用金額',
    couponTarget: '対象',
    couponTargetAll: '全商品',
    couponTargetCategory: '特定カテゴリ',
    couponTargetProduct: '特定商品',
    couponMemo: 'メモ',
    couponActive: '有効',
    couponInactive: '無効',
    couponExpired: '期限切れ',
    couponUsedUp: '使用済み',
    couponTotal: '合計',
    couponActiveCount: '有効数',
    couponTotalUsage: '総使用回数',
    couponTotalDiscount: '割引総額',
    couponAutoGenerate: '自動生成',
    couponCopyCode: 'コードをコピー',
    couponCodeCopied: 'コードをコピーしました',
    couponNoExpiry: '無期限',
    couponUnlimited: '無制限',
    couponDeleteConfirm: 'クーポンの削除',
    couponDeleteMessage: '以下のクーポンを削除しますか',
    couponCreated: 'クーポンを作成しました',
    couponUpdated: 'クーポンを更新しました',
    couponDeleted: 'クーポンを削除しました',
    couponLoadError: 'クーポンの読み込みに失敗しました',
    couponCodeRequired: 'クーポンコードを入力してください',
    couponValueRequired: '割引値を入力してください',
    couponPercentageMax: '割引率は100%以下にしてください',
    couponCodeDuplicate: '同じコードのクーポンが既に存在します',
    couponSearchPlaceholder: 'コード・メモで検索',

    // ============ 予約関連 ============
    newReservation: '新規予約',
    pending: '承認待ち',
    confirmed: '確定',
    cancelled: 'キャンセル',
    completed: '完了',
    noShow: '無断キャンセル',
    reservationDate: '予約日',
    reservationTime: '予約時間',
    numberOfPeople: '人数',
    customerName: 'お客様名',
    menuName: 'メニュー名',
    staffName: '担当スタッフ',
    approveReservation: '予約を承認',
    cancelReservation: '予約をキャンセル',
    reservationApproved: '予約を承認しました',
    reservationCancelled: '予約をキャンセルしました',
    reservationDetails: '予約詳細',

    // ============ 顧客関連 ============
    customerManagement: '顧客管理',
    customerList: '顧客一覧',
    customerDetails: '顧客詳細',
    totalVisits: '来店回数',
    lastVisit: '最終来店日',
    customerNotes: '顧客メモ',

    // ============ 従業員関連 ============
    employeeManagement: '従業員管理',
    employeeList: '従業員一覧',
    addEmployee: '従業員を追加',
    editEmployee: '従業員を編集',
    employeeName: '従業員名',
    role: '役職',
    owner: 'オーナー',
    manager: 'マネージャー',
    staffRole: 'スタッフ',
    workStatus: '勤務状況',
    working: '出勤中',
    notWorking: '退勤中',

    // ============ シフト関連 ============
    shiftManagement: 'シフト管理',
    shiftList: 'シフト一覧',
    createShift: 'シフトを作成',
    shiftDate: 'シフト日',
    shiftTime: 'シフト時間',
    startTime: '開始時刻',
    endTime: '終了時刻',

    // ============ 勤怠関連 ============
    attendanceManagement: '勤怠管理',
    attendanceList: '勤怠一覧',
    clockIn: '出勤',
    clockOut: '退勤',
    workingHours: '勤務時間',
    breakTime: '休憩時間',

    // ============ メニュー関連 ============
    menuManagement: 'メニュー管理',
    menuList: 'メニュー一覧',
    addMenu: 'メニューを追加',
    editMenu: 'メニューを編集',
    menuPrice: '価格',
    duration: '所要時間',

    // ============ 商品関連（モバイルオーダー） ============
    productList: '商品一覧',
    addProduct: '商品を追加',
    editProduct: '商品を編集',
    productName: '商品名',
    productPrice: '価格',
    productCategory: 'カテゴリ',
    onSale: '販売中',
    soldOut: '売り切れ',
    hidden: '非表示',
    noDescription: '説明なし',
    uncategorized: '未分類',
    spicy: '辛い',
    vegetarian: 'ベジタリアン',
    optionCount: 'オプション',
    batchTranslate: '一括翻訳',
    translating: '翻訳中...',
    translateConfirm: '全{count}件の商品を一括翻訳しますか？',

    // ============ 注文関連 ============
    orders: '注文管理',
    orderList: '注文一覧',
    orderDetails: '注文詳細',
    orderNumber: '注文番号',
    tableNumber: 'テーブル番号',
    orderedAt: '注文時刻',
    cooking: '調理中',
    ready: '準備完了',
    served: '提供済み',
    paid: '会計済み',

    // ============ 会計関連 ============
    payment: '支払い',
    paymentMethod: '支払い方法',
    cash: '現金',
    creditCard: 'クレジットカード',
    qrPayment: 'QRコード決済',
    electronicMoney: '電子マネー',
    other: 'その他',
    receivedAmount: 'お預かり',
    change: 'お釣り',
    completePayment: '会計完了',
    paymentCompleted: '会計が完了しました',
    receipt: 'レシート',
    printReceipt: 'レシート印刷',

    // ============ テーブル関連 ============
    tables: 'テーブル管理',
    tableList: 'テーブル一覧',
    addTable: 'テーブルを追加',
    editTable: 'テーブルを編集',
    tableStatus: 'テーブル状況',
    available: '空席',
    occupied: '使用中',
    reserved: '予約済み',
    cleaning: '清掃中',
    capacity: '定員',

    // ============ 言語 ============
    language: '言語設定',
    japanese: '日本語',
    english: 'English',
    thai: 'ไทย',
    selectLanguage: '言語を選択',

    // ============ 設定関連 ============
    shopManagement: '店舗管理',
    posSection: 'POSレジ・会計',
    mobileOrderSection: 'モバイルオーダー',
    shopName: '店舗名',
    shopCode: '店舗コード',
    businessHours: '営業時間',
    openTime: '開店時間',
    closeTime: '閉店時間',
    regularHoliday: '定休日',
    saveSettings: '設定を保存',
    settingsSaved: '設定を保存しました',

    // ============ エラーメッセージ ============
    loginFailed: 'ログインに失敗しました',
    sessionExpired: 'セッションが切れました。再度ログインしてください。',
    networkError: 'ネットワークエラーが発生しました',
    permissionDenied: '権限がありません',
    dataNotFound: 'データが見つかりません',
    saveFailed: '保存に失敗しました',
    deleteFailed: '削除に失敗しました',
    invalidInput: '入力内容が正しくありません',
  },

  // ============================================================
  // ENGLISH
  // ============================================================
  en: {
    // Dashboard
    dashboard: 'Dashboard',
    reservationManagement: 'Reservation Management',
    statistics: 'Statistics',
    totalReservations: 'Total Reservations',
    todayReservations: "Today's Reservations",
    monthlyReservations: 'Monthly Reservations',
    totalStaff: 'Total Staff',
    reservationQRCode: 'Reservation QR Code',
    qrCodeDescription: 'Scan this QR code to access the reservation page',
    downloadQRCode: 'Download QR Code',
    printQRCode: 'Print QR Code',
    viewReservations: 'View Reservations',
    salesAnalytics: 'Sales Analytics',
    todaySales: "Today's Sales",
    monthlySales: 'Monthly Sales',
    totalOrders: 'Total Orders',

    // Navigation
    home: 'Home',
    reservations: 'Reservations',
    menus: 'Menus',
    staff: 'Staff',
    customers: 'Customers',
    analytics: 'Analytics',
    floorMap: 'Floor Map',
    backApproval: 'Back Approval',
    settings: 'Settings',
    logout: 'Logout',
    employees: 'Employees',
    staffUsers: 'App Accounts',
    shifts: 'Shifts',
    attendances: 'Attendance',
    planSettings: 'Plan Settings',
    timeChargeSettings: 'Time-based Seat Charge',
    allShops: 'All Shops',
    salesReport: 'Sales Report',
    stripeConnect: 'Stripe Connect',
    reservationSettings: 'Reservation Settings',
    homepageSettings: 'Homepage Settings',
    shopSettings: 'Shop Settings',
    lineSettings: 'LINE Settings',
    nightPro: 'Night Pro Mode',
    receivables: 'Receivables',
    bottleKeeps: 'Bottle Keeps',
    proxyOrder: 'Staff Order',
    allowanceTypes: 'Allowances',
    payouts: 'Daily Payouts',
    payroll: 'Payroll',
    setupCheck: 'Setup Check',
    posRegister: 'POS Register',
    checkout: 'Checkout',
    checkoutRegister: 'Checkout',
    orderManagement: 'Order Management',
    tableManagement: 'Table Management',
    blacklist: 'Blacklist',
    productCategories: 'Product Categories',
    optionManagement: 'Option Management',
    productManagement: 'Product Management',
    planManagement: 'Plan Management',
    printerSettings: 'Printer Settings',
    mobileOrderSettings: 'Mobile Order Settings',

    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    create: 'Create',
    update: 'Update',
    detail: 'Detail',
    list: 'List',
    all: 'All',
    noData: 'No data',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    time: 'Time',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    price: 'Price',
    amount: 'Amount',
    total: 'Total',
    subtotal: 'Subtotal',
    tax: 'Tax',
    quantity: 'Qty',
    description: 'Description',
    notes: 'Notes',
    required: 'Required',
    optional: 'Optional',

    // Coupons
    couponManagement: 'Coupon Management',
    couponCreate: 'Create Coupon',
    couponEdit: 'Edit Coupon',
    couponCode: 'Coupon Code',
    couponDiscount: 'Discount',
    couponDiscountType: 'Discount Type',
    couponPercentage: 'Percentage (%)',
    couponFixed: 'Fixed Amount',
    couponPercentageValue: 'Discount Rate (%)',
    couponFixedValue: 'Discount Amount',
    couponExpiry: 'Expiry Date',
    couponExpiryHelp: 'Leave empty for no expiry',
    couponUsage: 'Usage',
    couponMaxUsage: 'Max Usage',
    couponMaxUsageHelp: '0 for unlimited',
    couponMinOrder: 'Min Order Amount',
    couponTarget: 'Target',
    couponTargetAll: 'All Products',
    couponTargetCategory: 'Specific Category',
    couponTargetProduct: 'Specific Product',
    couponMemo: 'Memo',
    couponActive: 'Active',
    couponInactive: 'Inactive',
    couponExpired: 'Expired',
    couponUsedUp: 'Used Up',
    couponTotal: 'Total',
    couponActiveCount: 'Active',
    couponTotalUsage: 'Total Usage',
    couponTotalDiscount: 'Total Discount',
    couponAutoGenerate: 'Generate',
    couponCopyCode: 'Copy Code',
    couponCodeCopied: 'Code copied',
    couponNoExpiry: 'No Expiry',
    couponUnlimited: 'Unlimited',
    couponDeleteConfirm: 'Delete Coupon',
    couponDeleteMessage: 'Delete this coupon?',
    couponCreated: 'Coupon created',
    couponUpdated: 'Coupon updated',
    couponDeleted: 'Coupon deleted',
    couponLoadError: 'Failed to load coupons',
    couponCodeRequired: 'Coupon code is required',
    couponValueRequired: 'Discount value is required',
    couponPercentageMax: 'Percentage must be 100% or less',
    couponCodeDuplicate: 'A coupon with this code already exists',
    couponSearchPlaceholder: 'Search by code or memo',

    // Events
    eventManagement: 'Event Management',
    events: 'Events',
    addEvent: 'Add Event',
    editEvent: 'Edit Event',
    eventType: 'Event Type',
    upcoming: 'Upcoming',
    past: 'Past',
    displayPeriod: 'Display Period',
    startDate: 'Start Date',
    endDate: 'End Date',
    images: 'Images',
    mainImage: 'Main Image',
    bannerImage: 'Banner Image',
    active: 'Active',
    reservationRequired: 'Reservation Required',
    showOnCalendar: 'Show on Calendar',
    showBanner: 'Show Banner',
    bannerPosition: 'Banner Position',
    heroPosition: 'Hero (Top)',
    middlePosition: 'Middle',
    bottomPosition: 'Bottom',
    remaining: '',
    seats: ' left',
    hide: 'Hide',
    show: 'Show',
    deleteConfirm: 'Are you sure you want to delete this event?',
    title: 'Title',

    // Reservations
    newReservation: 'New Reservation',
    pending: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    completed: 'Completed',
    noShow: 'No Show',
    reservationDate: 'Date',
    reservationTime: 'Time',
    numberOfPeople: 'Guests',
    customerName: 'Customer Name',
    menuName: 'Menu',
    staffName: 'Staff',
    approveReservation: 'Approve',
    cancelReservation: 'Cancel Reservation',
    reservationApproved: 'Reservation approved',
    reservationCancelled: 'Reservation cancelled',
    reservationDetails: 'Reservation Details',

    // Customers
    customerManagement: 'Customer Management',
    customerList: 'Customer List',
    customerDetails: 'Customer Details',
    totalVisits: 'Total Visits',
    lastVisit: 'Last Visit',
    customerNotes: 'Notes',

    // Employees
    employeeManagement: 'Employee Management',
    employeeList: 'Employee List',
    addEmployee: 'Add Employee',
    editEmployee: 'Edit Employee',
    employeeName: 'Name',
    role: 'Role',
    owner: 'Owner',
    manager: 'Manager',
    staffRole: 'Staff',
    workStatus: 'Work Status',
    working: 'Working',
    notWorking: 'Not Working',

    // Shifts
    shiftManagement: 'Shift Management',
    shiftList: 'Shift List',
    createShift: 'Create Shift',
    shiftDate: 'Date',
    shiftTime: 'Time',
    startTime: 'Start',
    endTime: 'End',

    // Attendance
    attendanceManagement: 'Attendance Management',
    attendanceList: 'Attendance List',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    workingHours: 'Working Hours',
    breakTime: 'Break Time',

    // Menus
    menuManagement: 'Menu Management',
    menuList: 'Menu List',
    addMenu: 'Add Menu',
    editMenu: 'Edit Menu',
    menuPrice: 'Price',
    duration: 'Duration',

    // Products (Mobile Order)
    productList: 'Product List',
    addProduct: 'Add Product',
    editProduct: 'Edit Product',
    productName: 'Product Name',
    productPrice: 'Price',
    productCategory: 'Category',
    onSale: 'On Sale',
    soldOut: 'Sold Out',
    hidden: 'Hidden',
    noDescription: 'No description',
    uncategorized: 'Uncategorized',
    spicy: 'Spicy',
    vegetarian: 'Vegetarian',
    optionCount: 'Options',
    batchTranslate: 'Batch Translate',
    translating: 'Translating...',
    translateConfirm: 'Translate all {count} products?',

    // Orders
    orders: 'Orders',
    orderList: 'Order List',
    orderDetails: 'Order Details',
    orderNumber: 'Order No.',
    tableNumber: 'Table No.',
    orderedAt: 'Ordered At',
    cooking: 'Cooking',
    ready: 'Ready',
    served: 'Served',
    paid: 'Paid',

    // Payment
    payment: 'Payment',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    creditCard: 'Credit Card',
    qrPayment: 'QR Payment',
    electronicMoney: 'E-Money',
    other: 'Other',
    receivedAmount: 'Received',
    change: 'Change',
    completePayment: 'Complete Payment',
    paymentCompleted: 'Payment completed',
    receipt: 'Receipt',
    printReceipt: 'Print Receipt',

    // Tables
    tables: 'Tables',
    tableList: 'Table List',
    addTable: 'Add Table',
    editTable: 'Edit Table',
    tableStatus: 'Status',
    available: 'Available',
    occupied: 'Occupied',
    reserved: 'Reserved',
    cleaning: 'Cleaning',
    capacity: 'Capacity',

    // Language
    language: 'Language',
    japanese: '日本語',
    english: 'English',
    thai: 'ไทย',
    selectLanguage: 'Select Language',

    // Settings
    shopManagement: 'Shop Management',
    posSection: 'POS/Checkout',
    mobileOrderSection: 'Mobile Order',
    shopName: 'Shop Name',
    shopCode: 'Shop Code',
    businessHours: 'Business Hours',
    openTime: 'Open',
    closeTime: 'Close',
    regularHoliday: 'Regular Holiday',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved',

    // Error Messages
    loginFailed: 'Login failed',
    sessionExpired: 'Session expired. Please login again.',
    networkError: 'Network error',
    permissionDenied: 'Permission denied',
    dataNotFound: 'Data not found',
    saveFailed: 'Save failed',
    deleteFailed: 'Delete failed',
    invalidInput: 'Invalid input',
  },

  // ============================================================
  // THAI
  // ============================================================
  th: {
    // แดชบอร์ด
    dashboard: 'แดชบอร์ด',
    reservationManagement: 'จัดการการจอง',
    statistics: 'สถิติ',
    totalReservations: 'การจองทั้งหมด',
    todayReservations: 'การจองวันนี้',
    monthlyReservations: 'การจองเดือนนี้',
    totalStaff: 'จำนวนพนักงาน',
    reservationQRCode: 'QR Code การจอง',
    qrCodeDescription: 'สแกน QR Code นี้เพื่อเข้าสู่หน้าจอง',
    downloadQRCode: 'ดาวน์โหลด QR Code',
    printQRCode: 'พิมพ์ QR Code',
    viewReservations: 'ดูการจอง',
    salesAnalytics: 'วิเคราะห์ยอดขาย',
    todaySales: 'ยอดขายวันนี้',
    monthlySales: 'ยอดขายเดือนนี้',
    totalOrders: 'คำสั่งซื้อทั้งหมด',

    // การนำทาง
    home: 'หน้าหลัก',
    reservations: 'การจอง',
    menus: 'เมนู',
    staff: 'พนักงาน',
    customers: 'ลูกค้า',
    analytics: 'วิเคราะห์',
    floorMap: 'ผังโต๊ะ',
    backApproval: 'อนุมัติแบ็ค',
    settings: 'ตั้งค่า',
    logout: 'ออกจากระบบ',
    employees: 'พนักงาน',
    staffUsers: 'บัญชีแอป',
    shifts: 'กะการทำงาน',
    attendances: 'การเข้างาน',
    planSettings: 'ตั้งค่าแผน',
    timeChargeSettings: 'ค่าที่นั่งตามเวลา',
    allShops: 'รายการร้าน',
    salesReport: 'รายงานยอดขาย',
    stripeConnect: 'เชื่อมต่อ Stripe',
    reservationSettings: 'ตั้งค่าการจอง',
    homepageSettings: 'ตั้งค่าหน้าแรก',
    shopSettings: 'ตั้งค่าร้าน',
    lineSettings: 'ตั้งค่า LINE',
    nightPro: 'โหมด Night Pro',
    receivables: 'ลูกหนี้',
    bottleKeeps: 'ขวดฝาก',
    proxyOrder: 'สั่งแทน',
    allowanceTypes: 'เบี้ยเลี้ยง',
    payouts: 'จ่ายรายวัน',
    payroll: 'เงินเดือน',
    setupCheck: 'ตรวจสอบการตั้งค่า',
    posRegister: 'เครื่องคิดเงิน',
    checkout: 'ชำระเงิน',
    checkoutRegister: 'ชำระเงิน',
    orderManagement: 'จัดการคำสั่งซื้อ',
    tableManagement: 'จัดการโต๊ะ',
    blacklist: 'บัญชีดำ',
    productCategories: 'หมวดหมู่สินค้า',
    optionManagement: 'จัดการตัวเลือก',
    productManagement: 'จัดการสินค้า',
    planManagement: 'จัดการแผน',
    printerSettings: 'ตั้งค่าเครื่องพิมพ์',
    mobileOrderSettings: 'ตั้งค่าสั่งผ่านมือถือ',

    // ทั่วไป
    save: 'บันทึก',
    cancel: 'ยกเลิก',
    delete: 'ลบ',
    edit: 'แก้ไข',
    add: 'เพิ่ม',
    search: 'ค้นหา',
    filter: 'กรอง',
    close: 'ปิด',
    back: 'กลับ',
    next: 'ถัดไป',
    previous: 'ก่อนหน้า',
    loading: 'กำลังโหลด...',
    error: 'เกิดข้อผิดพลาด',
    success: 'สำเร็จ',
    confirm: 'ยืนยัน',
    yes: 'ใช่',
    no: 'ไม่',
    ok: 'ตกลง',
    create: 'สร้าง',
    update: 'อัปเดต',
    detail: 'รายละเอียด',
    list: 'รายการ',
    all: 'ทั้งหมด',
    noData: 'ไม่มีข้อมูล',
    actions: 'ดำเนินการ',
    status: 'สถานะ',
    date: 'วันที่',
    time: 'เวลา',
    name: 'ชื่อ',
    email: 'อีเมล',
    phone: 'โทรศัพท์',
    address: 'ที่อยู่',
    price: 'ราคา',
    amount: 'จำนวนเงิน',
    total: 'รวมทั้งหมด',
    subtotal: 'ยอดรวม',
    tax: 'ภาษี',
    quantity: 'จำนวน',
    description: 'คำอธิบาย',
    notes: 'หมายเหตุ',
    required: 'จำเป็น',
    optional: 'ไม่บังคับ',

    // คูปอง
    couponManagement: 'จัดการคูปอง',
    couponCreate: 'สร้างคูปอง',
    couponEdit: 'แก้ไขคูปอง',
    couponCode: 'รหัสคูปอง',
    couponDiscount: 'ส่วนลด',
    couponDiscountType: 'ประเภทส่วนลด',
    couponPercentage: 'เปอร์เซ็นต์ (%)',
    couponFixed: 'จำนวนเงินคงที่',
    couponPercentageValue: 'อัตราส่วนลด (%)',
    couponFixedValue: 'จำนวนส่วนลด',
    couponExpiry: 'วันหมดอายุ',
    couponExpiryHelp: 'เว้นว่างไว้หากไม่มีวันหมดอายุ',
    couponUsage: 'การใช้งาน',
    couponMaxUsage: 'จำนวนการใช้งานสูงสุด',
    couponMaxUsageHelp: '0 สำหรับไม่จำกัด',
    couponMinOrder: 'ยอดสั่งซื้อขั้นต่ำ',
    couponTarget: 'เป้าหมาย',
    couponTargetAll: 'สินค้าทั้งหมด',
    couponTargetCategory: 'หมวดหมู่เฉพาะ',
    couponTargetProduct: 'สินค้าเฉพาะ',
    couponMemo: 'บันทึก',
    couponActive: 'ใช้งาน',
    couponInactive: 'ไม่ใช้งาน',
    couponExpired: 'หมดอายุ',
    couponUsedUp: 'ใช้หมดแล้ว',
    couponTotal: 'ทั้งหมด',
    couponActiveCount: 'ใช้งานอยู่',
    couponTotalUsage: 'ใช้งานทั้งหมด',
    couponTotalDiscount: 'ส่วนลดทั้งหมด',
    couponAutoGenerate: 'สร้างอัตโนมัติ',
    couponCopyCode: 'คัดลอกรหัส',
    couponCodeCopied: 'คัดลอกรหัสแล้ว',
    couponNoExpiry: 'ไม่มีวันหมดอายุ',
    couponUnlimited: 'ไม่จำกัด',
    couponDeleteConfirm: 'ลบคูปอง',
    couponDeleteMessage: 'ลบคูปองนี้?',
    couponCreated: 'สร้างคูปองแล้ว',
    couponUpdated: 'อัปเดตคูปองแล้ว',
    couponDeleted: 'ลบคูปองแล้ว',
    couponLoadError: 'โหลดคูปองไม่สำเร็จ',
    couponCodeRequired: 'ต้องระบุรหัสคูปอง',
    couponValueRequired: 'ต้องระบุมูลค่าส่วนลด',
    couponPercentageMax: 'เปอร์เซ็นต์ต้องไม่เกิน 100%',
    couponCodeDuplicate: 'คูปองรหัสนี้มีอยู่แล้ว',
    couponSearchPlaceholder: 'ค้นหาด้วยรหัสหรือบันทึก',

    // กิจกรรม
    eventManagement: 'จัดการกิจกรรม',
    events: 'กิจกรรม',
    addEvent: 'เพิ่มกิจกรรม',
    editEvent: 'แก้ไขกิจกรรม',
    eventType: 'ประเภทกิจกรรม',
    upcoming: 'กำลังจะมาถึง',
    past: 'ผ่านมาแล้ว',
    displayPeriod: 'ช่วงเวลาแสดง',
    startDate: 'วันเริ่มต้น',
    endDate: 'วันสิ้นสุด',
    images: 'รูปภาพ',
    mainImage: 'รูปหลัก',
    bannerImage: 'รูปแบนเนอร์',
    active: 'เปิดใช้งาน',
    reservationRequired: 'ต้องจอง',
    showOnCalendar: 'แสดงบนปฏิทิน',
    showBanner: 'แสดงแบนเนอร์',
    bannerPosition: 'ตำแหน่งแบนเนอร์',
    heroPosition: 'ด้านบน',
    middlePosition: 'กลาง',
    bottomPosition: 'ด้านล่าง',
    remaining: 'เหลือ ',
    seats: ' ที่นั่ง',
    hide: 'ซ่อน',
    show: 'แสดง',
    deleteConfirm: 'คุณแน่ใจว่าต้องการลบกิจกรรมนี้?',
    title: 'ชื่อ',

    // การจอง
    newReservation: 'การจองใหม่',
    pending: 'รอยืนยัน',
    confirmed: 'ยืนยันแล้ว',
    cancelled: 'ยกเลิก',
    completed: 'เสร็จสิ้น',
    noShow: 'ไม่มา',
    reservationDate: 'วันที่จอง',
    reservationTime: 'เวลาจอง',
    numberOfPeople: 'จำนวนคน',
    customerName: 'ชื่อลูกค้า',
    menuName: 'เมนู',
    staffName: 'พนักงาน',
    approveReservation: 'อนุมัติ',
    cancelReservation: 'ยกเลิกการจอง',
    reservationApproved: 'อนุมัติการจองแล้ว',
    reservationCancelled: 'ยกเลิกการจองแล้ว',
    reservationDetails: 'รายละเอียดการจอง',

    // ลูกค้า
    customerManagement: 'จัดการลูกค้า',
    customerList: 'รายชื่อลูกค้า',
    customerDetails: 'รายละเอียดลูกค้า',
    totalVisits: 'มาเยี่ยมทั้งหมด',
    lastVisit: 'มาล่าสุด',
    customerNotes: 'หมายเหตุ',

    // พนักงาน
    employeeManagement: 'จัดการพนักงาน',
    employeeList: 'รายชื่อพนักงาน',
    addEmployee: 'เพิ่มพนักงาน',
    editEmployee: 'แก้ไขพนักงาน',
    employeeName: 'ชื่อ',
    role: 'ตำแหน่ง',
    owner: 'เจ้าของ',
    manager: 'ผู้จัดการ',
    staffRole: 'พนักงาน',
    workStatus: 'สถานะการทำงาน',
    working: 'กำลังทำงาน',
    notWorking: 'ไม่ทำงาน',

    // กะการทำงาน
    shiftManagement: 'จัดการกะ',
    shiftList: 'รายการกะ',
    createShift: 'สร้างกะ',
    shiftDate: 'วันที่',
    shiftTime: 'เวลา',
    startTime: 'เริ่ม',
    endTime: 'สิ้นสุด',

    // การเข้างาน
    attendanceManagement: 'จัดการการเข้างาน',
    attendanceList: 'รายการเข้างาน',
    clockIn: 'ลงเวลาเข้า',
    clockOut: 'ลงเวลาออก',
    workingHours: 'ชั่วโมงทำงาน',
    breakTime: 'เวลาพัก',

    // เมนู
    menuManagement: 'จัดการเมนู',
    menuList: 'รายการเมนู',
    addMenu: 'เพิ่มเมนู',
    editMenu: 'แก้ไขเมนู',
    menuPrice: 'ราคา',
    duration: 'ระยะเวลา',

    // สินค้า (สั่งผ่านมือถือ)
    productList: 'รายการสินค้า',
    addProduct: 'เพิ่มสินค้า',
    editProduct: 'แก้ไขสินค้า',
    productName: 'ชื่อสินค้า',
    productPrice: 'ราคา',
    productCategory: 'หมวดหมู่',
    onSale: 'ขายอยู่',
    soldOut: 'หมดแล้ว',
    hidden: 'ซ่อน',
    noDescription: 'ไม่มีคำอธิบาย',
    uncategorized: 'ไม่มีหมวดหมู่',
    spicy: 'เผ็ด',
    vegetarian: 'มังสวิรัติ',
    optionCount: 'ตัวเลือก',
    batchTranslate: 'แปลทั้งหมด',
    translating: 'กำลังแปล...',
    translateConfirm: 'แปลสินค้าทั้งหมด {count} รายการ?',

    // คำสั่งซื้อ
    orders: 'คำสั่งซื้อ',
    orderList: 'รายการคำสั่งซื้อ',
    orderDetails: 'รายละเอียดคำสั่งซื้อ',
    orderNumber: 'เลขคำสั่งซื้อ',
    tableNumber: 'เลขโต๊ะ',
    orderedAt: 'เวลาสั่ง',
    cooking: 'กำลังปรุง',
    ready: 'พร้อมเสิร์ฟ',
    served: 'เสิร์ฟแล้ว',
    paid: 'ชำระแล้ว',

    // การชำระเงิน
    payment: 'การชำระเงิน',
    paymentMethod: 'วิธีชำระเงิน',
    cash: 'เงินสด',
    creditCard: 'บัตรเครดิต',
    qrPayment: 'ชำระผ่าน QR',
    electronicMoney: 'เงินอิเล็กทรอนิกส์',
    other: 'อื่นๆ',
    receivedAmount: 'รับเงิน',
    change: 'เงินทอน',
    completePayment: 'ชำระเงินเสร็จ',
    paymentCompleted: 'ชำระเงินเสร็จแล้ว',
    receipt: 'ใบเสร็จ',
    printReceipt: 'พิมพ์ใบเสร็จ',

    // โต๊ะ
    tables: 'โต๊ะ',
    tableList: 'รายการโต๊ะ',
    addTable: 'เพิ่มโต๊ะ',
    editTable: 'แก้ไขโต๊ะ',
    tableStatus: 'สถานะ',
    available: 'ว่าง',
    occupied: 'มีลูกค้า',
    reserved: 'จองแล้ว',
    cleaning: 'กำลังทำความสะอาด',
    capacity: 'ความจุ',

    // ภาษา
    language: 'ภาษา',
    japanese: '日本語',
    english: 'English',
    thai: 'ไทย',
    selectLanguage: 'เลือกภาษา',

    // ตั้งค่า
    shopManagement: 'จัดการร้าน',
    posSection: 'เครื่องคิดเงิน/ชำระ',
    mobileOrderSection: 'สั่งผ่านมือถือ',
    shopName: 'ชื่อร้าน',
    shopCode: 'รหัสร้าน',
    businessHours: 'เวลาทำการ',
    openTime: 'เปิด',
    closeTime: 'ปิด',
    regularHoliday: 'วันหยุดประจำ',
    saveSettings: 'บันทึกการตั้งค่า',
    settingsSaved: 'บันทึกการตั้งค่าแล้ว',

    // ข้อความข้อผิดพลาด
    loginFailed: 'เข้าสู่ระบบไม่สำเร็จ',
    sessionExpired: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง',
    networkError: 'ข้อผิดพลาดเครือข่าย',
    permissionDenied: 'ไม่ได้รับอนุญาต',
    dataNotFound: 'ไม่พบข้อมูล',
    saveFailed: 'บันทึกไม่สำเร็จ',
    deleteFailed: 'ลบไม่สำเร็จ',
    invalidInput: 'ข้อมูลไม่ถูกต้อง',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // 1. まずURLから言語を取得
    const urlLang = getLanguageFromPath();
    if (urlLang) {
      localStorage.setItem('language', urlLang);
      return urlLang;
    }
    // 2. URLになければローカルストレージから取得
    const saved = localStorage.getItem('language');
    return (saved === 'en' || saved === 'ja' || saved === 'th') ? saved as Language : 'ja';
  });

  /**
   * 言語はURLに書き戻さない。
   *
   * 以前は初回描画時に history.replaceState でパスへ /ja を差し込んでいた。
   * これが React Router と衝突し、画面が真っ白になる原因になっていた:
   *   - replaceState はルータに通知しないので、その場では正常に見える
   *   - 次の遷移やリロードで /ja/... のURLが評価され、
   *     言語リダイレクト → 言語の書き戻し → 再びリダイレクト を繰り返して何も描画されない
   * 実測では 23 画面中 7 画面が白くなっていた（会計・商品・カテゴリ・卓・注文・設定・プリンター）。
   *
   * 言語は localStorage で保持する。/ja/... のような外部リンクは
   * App.tsx の LanguageRedirect が受けて、プレフィックスを外した場所へ移す。
   */
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
